import { useState } from 'react';
import { Users, Trash2, UserPlus, FolderPlus, Eye, EyeOff, FolderMinus } from 'lucide-react';
import { useStore } from '@/store/StoreContext';
import { Card, SectionTitle, Button, Modal, TextInput, Label } from '@/components/ui';
import type { WorkPattern, ContractType } from '@/types';

export function DashboardView() {
  const { classes, activeClassId, activeClass, activeClassAgents, setActiveClassId, addClass, deleteClass, addAgent, deleteAgent, userRole } = useStore();

  const [isAddClassOpen, setIsAddClassOpen] = useState(false);
  const [isAddAgentOpen, setIsAddAgentOpen] = useState(false);

  const [newClassName, setNewClassName] = useState('');
  const [newClassStartDate, setNewClassStartDate] = useState(new Date().toISOString().slice(0, 10));

  const [name, setName] = useState('');
  const [pNumber, setPNumber] = useState('');
  const [email, setEmail] = useState('');
  const [personalPhone, setPersonalPhone] = useState('');
  const [personalEmail, setPersonalEmail] = useState('');
  const [isIrishSpeaking, setIsIrishSpeaking] = useState(false);
  const [workPattern, setWorkPattern] = useState<WorkPattern>('Hybrid');
  const [contractType, setContractType] = useState<ContractType>('Full-time');

  const [visiblePins, setVisiblePins] = useState<Record<string, boolean>>({});
  const [visibleContacts, setVisibleContacts] = useState<Record<string, boolean>>({});

  // Strictly enforce read-only status for Viewers, Guests, and Managers
  const isReadOnly = userRole === 'viewer' || userRole === 'guest' || userRole === 'manager';

  // Sort classes by startDate descending (latest date first)
  const sortedClasses = [...classes].sort(
    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
  );

  const togglePinVisibility = (agentId: string) => {
    setVisiblePins((prev) => ({ ...prev, [agentId]: !prev[agentId] }));
  };

  const toggleContactVisibility = (agentId: string) => {
    setVisibleContacts((prev) => ({ ...prev, [agentId]: !prev[agentId] }));
  };

  const handleCreateClass = (e: React.FormEvent) => {
    e.preventDefault();
    if (isReadOnly) return;
    if (!newClassName.trim()) return;
    addClass(newClassName.trim(), newClassStartDate);
    setNewClassName('');
    setIsAddClassOpen(false);
  };

  const handleDeleteActiveCohort = () => {
    if (isReadOnly) return;
    if (!activeClass) return;

    if (confirm(`Are you sure you want to completely remove "${activeClass.className}" and all associated trainee records? This action cannot be undone.`)) {
      deleteClass(activeClass.id);
    }
  };

  const handleAddAgent = (e: React.FormEvent) => {
    e.preventDefault();
    if (isReadOnly) return;
    if (!name.trim() || !pNumber.trim() || !email.trim()) return;

    addAgent(name.trim(), pNumber.trim(), email.trim(), {
      personalPhone: personalPhone.trim(),
      personalEmail: personalEmail.trim(),
      isIrishSpeaking,
      workPattern,
      contractType,
    });

    setName('');
    setPNumber('');
    setEmail('');
    setPersonalPhone('');
    setPersonalEmail('');
    setIsIrishSpeaking(false);
    setIsAddAgentOpen(false);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <SectionTitle
          title="Trainee Roster & Dashboard"
          subtitle={`Active Class: ${activeClass?.className || 'None Selected'} (Starts ${activeClass?.startDate || 'N/A'})`}
          icon={<Users className="w-6 h-6" />}
        />

        {!isReadOnly && (
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={() => setIsAddClassOpen(true)}>
              <FolderPlus className="w-4 h-4" /> New Cohort
            </Button>
            <Button onClick={() => setIsAddAgentOpen(true)}>
              <UserPlus className="w-4 h-4" /> Add Trainee
            </Button>
          </div>
        )}
      </div>

      <Card className="p-4 bg-white border border-slate-200">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Select Active Cohort</label>
          <div className="flex items-center gap-2">
            <select
              value={activeClassId || ''}
              onChange={(e) => setActiveClassId(e.target.value)}
              className="rounded-xl border border-slate-300 px-3.5 py-2 text-sm text-slate-900 bg-white font-medium focus:border-primary-500 focus:outline-none"
            >
              {sortedClasses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.className} (Starts {c.startDate})
                </option>
              ))}
            </select>

            {!isReadOnly && activeClass && (
              <button
                type="button"
                onClick={handleDeleteActiveCohort}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors cursor-pointer"
                title="Remove Active Cohort"
              >
                <FolderMinus className="w-4 h-4" /> Delete Cohort
              </button>
            )}
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse table-fixed">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/75 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <th className="px-6 py-3.5 w-48">Trainee Name</th>
                <th className="px-6 py-3.5 w-40">P-Number / PIN</th>
                <th className="px-6 py-3.5 w-56">Work Email</th>
                <th className="px-6 py-3.5 w-64">Personal Contact</th>
                <th className="px-6 py-3.5 w-32">Attributes</th>
                <th className="px-6 py-3.5 w-40">Pattern / Contract</th>
                {!isReadOnly && <th className="px-6 py-3.5 w-24 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {activeClassAgents.length === 0 ? (
                <tr>
                  <td colSpan={isReadOnly ? 6 : 7} className="px-6 py-12 text-center text-slate-400 text-sm">
                    No trainees added to this cohort yet.
                  </td>
                </tr>
              ) : (
                activeClassAgents.map((agent) => {
                  const isPinVisible = !!visiblePins[agent.id];
                  const isContactVisible = !!visibleContacts[agent.id];

                  return (
                    <tr key={agent.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-6 py-4 font-bold text-slate-900 truncate">{agent.name}</td>
                      
                      <td className="px-6 py-4 font-mono text-xs">
                        <div className="font-bold text-slate-800">{agent.pNumber}</div>
                        <div className="flex items-center gap-1.5 text-slate-500 mt-0.5">
                          <span>PIN: {isPinVisible ? agent.password : '••••'}</span>
                          <button
                            type="button"
                            onClick={() => togglePinVisibility(agent.id)}
                            className="text-slate-400 hover:text-slate-600 p-0.5 rounded transition-colors cursor-pointer"
                            title={isPinVisible ? "Hide PIN" : "Show PIN"}
                          >
                            {isPinVisible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>

                      <td className="px-6 py-4 text-xs text-slate-600 font-mono truncate">{agent.email}</td>

                      <td className="px-6 py-4 text-xs font-mono text-slate-600">
                        <div className="flex items-start gap-2 h-9">
                          <div className="overflow-hidden min-w-0 flex-1">
                            <div className="truncate font-semibold text-slate-800">
                              {isContactVisible ? (agent.personalPhone || 'No phone') : '••••••••••••'}
                            </div>
                            <div className="truncate text-slate-400 text-[11px]">
                              {isContactVisible ? (agent.personalEmail || 'No email') : '••••••••••••••••'}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleContactVisibility(agent.id)}
                            className="text-slate-400 hover:text-slate-600 p-0.5 rounded transition-colors cursor-pointer shrink-0 mt-0.5"
                            title={isContactVisible ? "Hide Contact Information" : "View Contact Information"}
                          >
                            {isContactVisible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        {agent.isIrishSpeaking ? (
                          <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold border border-emerald-200">
                            Irish-speaking
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-xs">
                        <div className="font-bold text-slate-700">{agent.workPattern}</div>
                        <div className="text-slate-400">{agent.contractType}</div>
                      </td>
                      {!isReadOnly && (
                        <td className="px-6 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => {
                              if (confirm(`Remove ${agent.name} from roster?`)) {
                                deleteAgent(agent.id);
                              }
                            }}
                            className="text-slate-400 hover:text-red-600 p-1.5 rounded-lg transition-colors cursor-pointer"
                            title="Remove Trainee"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add Class Modal */}
      <Modal open={isAddClassOpen} onClose={() => setIsAddClassOpen(false)} title="Create New Onboarding Cohort">
        <form onSubmit={handleCreateClass} className="space-y-4">
          <div>
            <Label>Cohort Name <span className="text-red-500">*</span></Label>
            <TextInput value={newClassName} onChange={setNewClassName} placeholder="e.g. September 2026 Onboarding Cohort" required />
          </div>
          <div>
            <Label>Start Date <span className="text-red-500">*</span></Label>
            <TextInput type="date" value={newClassStartDate} onChange={setNewClassStartDate} required />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setIsAddClassOpen(false)}>Cancel</Button>
            <Button type="submit">Create Cohort</Button>
          </div>
        </form>
      </Modal>

      {/* Add Agent Modal */}
      <Modal open={isAddAgentOpen} onClose={() => setIsAddAgentOpen(false)} title="Add Trainee to Roster">
        <form onSubmit={handleAddAgent} className="space-y-4">
          <div>
            <Label>Full Name <span className="text-red-500">*</span></Label>
            <TextInput value={name} onChange={setName} placeholder="e.g. Sean Murphy" required />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>P-Number <span className="text-red-500">*</span></Label>
              <TextInput value={pNumber} onChange={setPNumber} placeholder="e.g. P50001234" required />
            </div>
            <div>
              <Label>Work Email Address <span className="text-red-500">*</span></Label>
              <TextInput type="email" value={email} onChange={setEmail} placeholder="e.g. sean.murphy@uisce.ie" required />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-1 border-t border-slate-100">
            <div>
              <Label>Personal Phone Number <span className="text-xs text-slate-400 font-normal">(Optional)</span></Label>
              <TextInput value={personalPhone} onChange={setPersonalPhone} placeholder="e.g. +353 87 123 4567" />
            </div>
            <div>
              <Label>Personal Email Address <span className="text-xs text-slate-400 font-normal">(Optional)</span></Label>
              <TextInput type="email" value={personalEmail} onChange={setPersonalEmail} placeholder="e.g. sean.personal@gmail.com" />
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              id="irish-speaking"
              checked={isIrishSpeaking}
              onChange={(e) => setIsIrishSpeaking(e.target.checked)}
              className="rounded border-slate-300 text-primary-600 focus:ring-primary-500 w-4 h-4 cursor-pointer"
            />
            <label htmlFor="irish-speaking" className="text-xs font-semibold text-slate-700 cursor-pointer">
              Irish-speaking Trainee
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2">
            <div>
              <Label>Work Pattern</Label>
              <select
                value={workPattern}
                onChange={(e) => setWorkPattern(e.target.value as WorkPattern)}
                className="w-full rounded-lg border border-slate-300 p-2 text-xs font-bold text-slate-800 bg-white"
              >
                <option value="Hybrid">Hybrid</option>
                <option value="On-site">On-site</option>
                <option value="Remote">Remote</option>
              </select>
            </div>

            <div>
              <Label>Contract Type</Label>
              <select
                value={contractType}
                onChange={(e) => setContractType(e.target.value as ContractType)}
                className="w-full rounded-lg border border-slate-300 p-2 text-xs font-bold text-slate-800 bg-white"
              >
                <option value="Full-time">Full-time</option>
                <option value="Part-time">Part-time</option>
              </select>
            </div>
          </div>

          <div className="text-[11px] text-slate-400 italic pt-1">
            <span className="text-red-500 font-bold">*</span> Full Name, P-Number, and Work Email are required fields.
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <Button variant="secondary" onClick={() => setIsAddAgentOpen(false)}>Cancel</Button>
            <Button type="submit">Add Trainee</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
